/**
 * F-138: keep the board's storyboard folder current while the board is open.
 *
 * One hook on the board screen. While this board is enabled and a root
 * folder is chosen, every change to the doc (a keystroke, a peer's edit, a
 * dropped image, an unfurl landing) schedules a run 1.5 s out; runs never
 * overlap — a change that arrives mid-run marks the run dirty and one more
 * follows. The first run happens on enable (or on open, for a board enabled
 * earlier), so the folder is complete before anyone reads it.
 *
 * Row bands for slotting floating elements come from the grid's measured
 * layout when the grid is mounted (comments store `gridLayout`, the same
 * snapshot the comment pins resolve against); in Script view there is no
 * grid, and the builder falls back to stored heights. The line-kind labels
 * come from the workspace registry (features/text-kinds), which the board
 * screen refreshes on open.
 *
 * Failures reach the user ONCE per streak: a toast when a run first fails
 * (or first drops files), silence while it keeps failing the same way, and
 * the "⋯" menu shows the current state throughout.
 */
import type { ShortSlot } from "@kola/shared";
import { useEffect } from "react";
import { useStore } from "zustand";

import { fetchAssetBlob } from "../../core/api.js";
import { getHost } from "../../core/host.js";
import type { BoardSession } from "../../core/session.js";
import { showToast } from "../../ui/toast.js";
import type { CommentsUiStore } from "../comments/store.js";
import { textKinds } from "../text-kinds/store.js";
import { storyboardSync } from "./store.js";
import { runStoryboardSync } from "./sync.js";

/** How long after the last change a run starts. */
export const STORYBOARD_SYNC_DEBOUNCE_MS = 1500;

export function useStoryboardSync(
  session: BoardSession,
  boardName: string,
  commentsUi: CommentsUiStore,
  sheetId: string | null,
  /** F-142: a Shorts week's slots, so the manifest names each short; undefined elsewhere. */
  shorts?: readonly ShortSlot[],
): void {
  const host = getHost()?.storyboardSync;
  const store = storyboardSync;
  const enabled = useStore(store, (s) => s.enabled.includes(session.boardId));
  const rootDir = useStore(store, (s) => s.rootDir);
  // Not before the doc has synced: the first run must describe the board,
  // not the empty doc a session starts from.
  const synced = useStore(session.store, (s) => s.synced);

  useEffect(() => {
    if (host === undefined || !enabled || rootDir === null || sheetId === null || !synced) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let running = false;
    let dirty = false;
    let stopped = false;
    let lastFailure: string | null = null;

    const run = async (): Promise<void> => {
      if (stopped) return;
      if (running) {
        dirty = true;
        return;
      }
      running = true;
      dirty = false;
      store.getState().setStatus(session.boardId, { ...store.getState().status[session.boardId], phase: "syncing" });
      try {
        const result = await runStoryboardSync({
          board: session.board,
          sheetId,
          boardId: session.boardId,
          boardName,
          host,
          rootDir,
          kinds: textKinds.getState().kinds,
          rowBands: rowBandsFor(commentsUi, sheetId),
          fetchAsset: fetchAssetBlob,
          shorts,
        });
        if (stopped) return;
        if (!result.ok) {
          report(result.error ?? "Sync failed", true);
        } else if (result.failed.length > 0) {
          const n = result.failed.length;
          report(`${n} file${n === 1 ? "" : "s"} could not be downloaded`, false, result.dir);
        } else {
          lastFailure = null;
          store.getState().setStatus(session.boardId, { phase: "synced", at: Date.now(), dir: result.dir });
        }
      } catch (err) {
        if (!stopped) report(err instanceof Error ? err.message : String(err), true);
      } finally {
        running = false;
        if (dirty && !stopped) schedule();
      }
    };

    const report = (message: string, fatal: boolean, dir?: string): void => {
      const status = { phase: fatal ? ("error" as const) : ("synced" as const), at: Date.now(), message, dir };
      store.getState().setStatus(session.boardId, status);
      if (lastFailure !== message) {
        lastFailure = message;
        showToast(fatal ? `Storyboard sync failed: ${message}` : `Storyboard synced — ${message}`);
      }
    };

    const schedule = (): void => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void run();
      }, STORYBOARD_SYNC_DEBOUNCE_MS);
    };

    const onUpdate = (): void => schedule();
    session.board.doc.on("update", onUpdate);
    void run();

    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      session.board.doc.off("update", onUpdate);
    };
  }, [host, enabled, rootDir, sheetId, synced, session, boardName, commentsUi, store, shorts]);
}

/** Measured row bands from the mounted grid, for this sheet; undefined off-grid. */
function rowBandsFor(commentsUi: CommentsUiStore, sheetId: string) {
  const layout = commentsUi.getState().gridLayout;
  if (layout === null || layout.sheetId !== sheetId) return undefined;
  return layout.rowIds.map((rowId, i) => ({
    rowId,
    top: layout.originY + (layout.rowEdges[i] ?? 0),
    bottom: layout.originY + (layout.rowEdges[i + 1] ?? layout.rowEdges[i] ?? 0),
  }));
}
